/**
 * Run `cb` exactly once, at the earliest of: the browser going idle (bounded by
 * `timeout` so it always fires eventually) or the visitor's first interaction.
 *
 * Used to defer non-critical third-party JS (PostHog analytics, Meta Pixel) off
 * the initial render so their bundles + session recorder never block first paint
 * or inflate mobile Total Blocking Time. Firing on first interaction means any
 * engaged visitor still gets trackers loaded promptly; the timeout backstops
 * visitors who never interact. No-op during SSR/prerender (no `window`).
 */
export function whenIdle(cb: () => void, { timeout = 1500 }: { timeout?: number } = {}): void {
  if (typeof window === "undefined") return;

  let fired = false;
  const events = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
  const run = (): void => {
    if (fired) return;
    fired = true;
    for (const e of events) window.removeEventListener(e, run);
    cb();
  };

  for (const e of events) window.addEventListener(e, run, { once: true, passive: true });

  const ric = window.requestIdleCallback;
  if (typeof ric === "function") ric(run, { timeout });
  else window.setTimeout(run, timeout);
}
